import { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import { ContributionSchedule } from '../types';

interface ContributionScheduleCardProps {
  schedule: ContributionSchedule;
  onUpdate: (schedule: Partial<ContributionSchedule>) => void;
}

const ContributionScheduleCard = ({ schedule, onUpdate }: ContributionScheduleCardProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(schedule);

  // Update formData when schedule prop changes
  useEffect(() => {
    setFormData(schedule);
  }, [schedule]);

  const daysOfWeek = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];

  const handleDayToggle = (day: string) => {
    const newDays = formData.days.includes(day)
      ? formData.days.filter((d) => d !== day)
      : [...formData.days, day];
    setFormData({ ...formData, days: newDays });
  };

  const handleSave = () => {
    // Only require days if frequency is weekly or biweekly
    const isWeeklyOrBiweekly = formData.contributionFrequency === 'weekly' || formData.contributionFrequency === 'biweekly';
    
    if (isWeeklyOrBiweekly && formData.days.length === 0) {
      alert('Please select at least one day for weekly/biweekly contributions');
      return;
    }
    
    // For monthly/quarterly, clear days array since they use collection_day/lending_day instead
    if (!isWeeklyOrBiweekly) {
      formData.days = [];
    }
    
    onUpdate(formData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setFormData(schedule);
    setIsEditing(false);
  };

  return (
    <div className="bg-card rounded-md border border-border p-6 shadow-elevation-1">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-accent/10 rounded-md flex items-center justify-center">
            <Icon name="Calendar" size={24} color="var(--color-accent)" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground">Contribution Schedule</h2>
            <p className="text-sm text-muted-foreground">
              Configure when contributions can be recorded
            </p>
          </div>
        </div>
        {!isEditing && (
          <Button
            variant="outline"
            iconName="Edit"
            iconPosition="left"
            onClick={() => setIsEditing(true)}
          >
            Edit
          </Button>
        )}
      </div>

      <div className="space-y-6">
        {isEditing ? (
          <>
            {/* Mobile Schedule Fields - Show first */}
            <div className="border-b border-border pb-4">
              <h3 className="text-sm font-semibold text-foreground mb-4">Collection & Lending Schedule</h3>

              <div className="flex flex-col gap-3 mb-4 p-3 rounded-md bg-muted/30 border border-border">
                <p className="text-xs text-muted-foreground mb-1">Turn off to disable recording or lending for this group</p>
                <div className="flex items-center gap-4">
                  <Checkbox
                    id="contributions-enabled"
                    checked={formData.contributionsEnabled !== false}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, contributionsEnabled: checked !== false })
                    }
                  />
                  <label htmlFor="contributions-enabled" className="text-sm font-medium text-foreground cursor-pointer">
                    Contributions enabled
                  </label>
                </div>
                <div className="flex items-center gap-4">
                  <Checkbox
                    id="lending-enabled"
                    checked={formData.lendingEnabled !== false}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, lendingEnabled: checked !== false })
                    }
                  />
                  <label htmlFor="lending-enabled" className="text-sm font-medium text-foreground cursor-pointer">
                    Lending enabled
                  </label>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Contribution Amount
                  </label>
                  <div className="flex gap-2">
                    <select
                      value={formData.currency || 'VT'}
                      onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                      className="w-20 px-3 py-2 border border-input rounded-sm bg-background text-foreground"
                    >
                      <option value="VT">VT</option>
                      <option value="USD">USD</option>
                      <option value="AUD">AUD</option>
                      <option value="NZD">NZD</option>
                    </select>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.contributionAmount || 0}
                      onChange={(e) =>
                        setFormData({ ...formData, contributionAmount: parseFloat(e.target.value) || 0 })
                      }
                      className="flex-1 px-3 py-2 border border-input rounded-sm bg-background text-foreground"
                      placeholder="0.00"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Collection Frequency
                  </label>
                  <select
                    value={formData.contributionFrequency || 'monthly'}
                    onChange={(e) => {
                      const newFrequency = e.target.value;
                      // Clear days if switching to monthly/quarterly
                      const newDays = (newFrequency === 'weekly' || newFrequency === 'biweekly') 
                        ? formData.days 
                        : [];
                      setFormData({ ...formData, contributionFrequency: newFrequency, days: newDays });
                    }}
                    className="w-full px-3 py-2 border border-input rounded-sm bg-background text-foreground"
                  >
                    <option value="weekly">Weekly</option>
                    <option value="biweekly">Biweekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Money Collection Day (1-31)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="31"
                    value={formData.collectionDay || ''}
                    onChange={(e) =>
                      setFormData({ ...formData, collectionDay: e.target.value ? parseInt(e.target.value) : null })
                    }
                    className="w-full px-3 py-2 border border-input rounded-sm bg-background text-foreground"
                    placeholder="e.g., 15"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Money Lending Day (1-31)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="31"
                    value={formData.lendingDay || ''}
                    onChange={(e) =>
                      setFormData({ ...formData, lendingDay: e.target.value ? parseInt(e.target.value) : null })
                    }
                    className="w-full px-3 py-2 border border-input rounded-sm bg-background text-foreground"
                    placeholder="e.g., 15"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Membership Fee (One-time)
                </label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.membershipFee || 0}
                    onChange={(e) =>
                      setFormData({ ...formData, membershipFee: parseFloat(e.target.value) || 0 })
                    }
                    className="flex-1 px-3 py-2 border border-input rounded-sm bg-background text-foreground"
                    placeholder="0.00"
                  />
                  <span className="px-3 py-2 text-sm text-muted-foreground flex items-center">
                    {formData.currency || 'VT'}
                  </span>
                </div>
              </div>
            </div>

            {/* Active Days - Only show for weekly/biweekly */}
            {(formData.contributionFrequency === 'weekly' || formData.contributionFrequency === 'biweekly') && (
              <div>
                <label className="text-sm font-medium text-foreground mb-3 block">
                  Active Days
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {daysOfWeek.map((day) => (
                    <Checkbox
                      key={day}
                      label={day}
                      checked={formData.days.includes(day)}
                      onChange={() => handleDayToggle(day)}
                    />
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Recording Time
                </label>
                <input
                  type="time"
                  value={formData.time}
                  onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                  className="w-full px-3 py-2 border border-input rounded-sm bg-background text-foreground"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Timezone
                </label>
                <select
                  value={formData.timezone}
                  onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                  className="w-full px-3 py-2 border border-input rounded-sm bg-background text-foreground"
                >
                  <option value="Pacific/Port_Vila">Vanuatu (Pacific/Port_Vila)</option>
                  <option value="America/New_York">Eastern Time (ET)</option>
                  <option value="America/Chicago">Central Time (CT)</option>
                  <option value="America/Denver">Mountain Time (MT)</option>
                  <option value="America/Los_Angeles">Pacific Time (PT)</option>
                </select>
              </div>
            </div>

            <div className="border-t border-border pt-4">
              <Checkbox
                label="Enable Reminders"
                description="Send notifications before contribution recording opens"
                checked={formData.reminderEnabled}
                onChange={(e) =>
                  setFormData({ ...formData, reminderEnabled: e.target.checked })
                }
              />
              {formData.reminderEnabled && (
                <div className="mt-4 ml-6">
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Reminder Time
                  </label>
                  <input
                    type="time"
                    value={formData.reminderTime}
                    onChange={(e) =>
                      setFormData({ ...formData, reminderTime: e.target.value })
                    }
                    className="w-full max-w-xs px-3 py-2 border border-input rounded-sm bg-background text-foreground"
                  />
                </div>
              )}
            </div>


            <div className="flex gap-3 pt-2">
              <Button variant="default" onClick={handleSave}>
                Save Schedule
              </Button>
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </>
        ) : (
          <>
            {/* Mobile Schedule Display - Show first */}
            {(schedule.collectionDay || schedule.lendingDay || schedule.contributionAmount || schedule.contributionFrequency || schedule.contributionsEnabled === false || schedule.lendingEnabled === false) && (
              <div className="border-b border-border pb-4 mb-4">
                <h3 className="text-sm font-semibold text-foreground mb-4">Collection & Lending Schedule</h3>
                <div className="flex flex-wrap gap-4 mb-4 text-sm">
                  <span className={schedule.contributionsEnabled !== false ? 'text-success' : 'text-muted-foreground'}>
                    Contributions: {schedule.contributionsEnabled !== false ? 'On' : 'Off'}
                  </span>
                  <span className={schedule.lendingEnabled !== false ? 'text-success' : 'text-muted-foreground'}>
                    Lending: {schedule.lendingEnabled !== false ? 'On' : 'Off'}
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {schedule.contributionAmount !== undefined && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Contribution Amount</label>
                      <p className="text-base text-foreground mt-1">
                        {schedule.currency || 'VT'} {schedule.contributionAmount.toLocaleString()}
                      </p>
                    </div>
                  )}
                  {schedule.contributionFrequency && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Frequency</label>
                      <p className="text-base text-foreground mt-1 capitalize">{schedule.contributionFrequency}</p>
                    </div>
                  )}
                  {schedule.collectionDay && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Collection Day</label>
                      <p className="text-base text-foreground mt-1">{schedule.collectionDay}th of month</p>
                    </div>
                  )}
                  {schedule.lendingDay && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Lending Day</label>
                      <p className="text-base text-foreground mt-1">{schedule.lendingDay}th of month</p>
                    </div>
                  )}
                  {schedule.membershipFee !== undefined && schedule.membershipFee > 0 && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Membership Fee</label>
                      <p className="text-base text-foreground mt-1">
                        {schedule.currency || 'VT'} {schedule.membershipFee.toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Active Days - Only show for weekly/biweekly */}
            {(schedule.contributionFrequency === 'weekly' || schedule.contributionFrequency === 'biweekly') && schedule.days.length > 0 && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Active Days</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {schedule.days.map((day) => (
                    <span
                      key={day}
                      className="px-3 py-1 bg-primary/10 text-primary text-sm font-medium rounded-sm"
                    >
                      {day}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Recording Time
                </label>
                <p className="text-base text-foreground mt-1">{schedule.time}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Timezone</label>
                <p className="text-base text-foreground mt-1">{schedule.timezone}</p>
              </div>
            </div>

            {schedule.reminderEnabled && (
              <div className="border-t border-border pt-4">
                <div className="flex items-center gap-2 text-success">
                  <Icon name="Bell" size={18} />
                  <span className="text-sm font-medium">
                    Reminders enabled at {schedule.reminderTime}
                  </span>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ContributionScheduleCard;